/*
  <size-guide>
  ------------
  Custom element that owns the size-guide UI. Used in two surfaces:
    - inside a popup-component dialog (modal mode, on product pages)
    - directly in a section (embedded mode, on the Size Guide page)

  The element queries its own subtree only, so multiple instances on the
  same page do not collide. State is per-instance.

  PRODUCTS: catalog of size tables. The `id` field maps to a Shopify
  product. Resolution order from the snippet that mounts this element:
    1. block.settings.size_guide_id_override
    2. product.metafields.custom.size_guide_id
    3. product.metafields.theme.siblings.value (the siblings collection handle)
    4. current_variant.sku
    5. product.handle

  When a SKU or sibling handle changes in the store, set the metafield on
  the affected product instead of editing this file.
*/

(function () {
  'use strict';

  if (customElements.get('size-guide')) return;

  /* ---------- Data ---------- */

  var SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL'];

  /*
    EASE MODEL
      body = (flat × 2) − ease       when doubleForBody is true (circumference)
      body = flat                     when doubleForBody is false (linear)

    Polarity: positive ease → garment > body; negative ease → garment < body.
  */
  var PRODUCTS = [
    /* TOPS */
    {
      id: 'arow-lightweight-tee', name: 'AROW Lightweight Tee', category: 'Tops', type: 'upper',
      fitNote: 'Athletic fit. If between sizes, size up.',
      measurements: {
        'Chest':         { garment: [44, 46.5, 49, 51.5, 54, 56.5], doubleForBody: true, ease: 8, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [65, 66.5, 68, 69.5, 71, 72.5], doubleForBody: false, tip: 'High point shoulder to hem' },
        'Sleeve Length': { garment: [22.5, 23.5, 24.5, 25.5, 26.5, 27.5], doubleForBody: false, tip: 'Shoulder point to cuff' }
      }
    },
    {
      id: 'arow-lightweight-long-sleeve', name: 'AROW Lightweight Long Sleeve', category: 'Tops', type: 'upper',
      fitNote: 'Athletic fit. Same body as the Lightweight Tee with a long sleeve.',
      measurements: {
        'Chest':         { garment: [44, 46.5, 49, 51.5, 54, 56.5], doubleForBody: true, ease: 8, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [65, 66.5, 68, 69.5, 71, 72.5], doubleForBody: false, tip: 'High point shoulder to hem' },
        'Sleeve Length': { garment: [54, 55, 56, 57, 58, 59], doubleForBody: false, tip: 'Shoulder point to cuff' }
      }
    },
    {
      id: 'arow-singlet', name: 'AROW Singlet', category: 'Tops', type: 'upper',
      fitNote: 'Race-day fit. Body-skimming cut for movement and ventilation — noticeably narrower than the Tee.',
      measurements: {
        'Chest':         { garment: [40, 42.5, 45, 47.5, 50, 52.5], doubleForBody: true, ease: 0, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [61.5, 63, 64.5, 66, 67.5, 69], doubleForBody: false, tip: 'High point shoulder to hem' }
      }
    },
    {
      id: 'arow-ultra-tee', name: 'AROW Ultra Tee', category: 'Tops', type: 'upper',
      fitNote: 'Athletic fit — same pattern block as the Lightweight Tee, built for longer efforts.',
      measurements: {
        'Chest':         { garment: [44, 46.5, 49, 51.5, 54, 56.5], doubleForBody: true, ease: 8, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [65, 66.5, 68, 69.5, 71, 72.5], doubleForBody: false, tip: 'High point shoulder to hem' },
        'Sleeve Length': { garment: [22.5, 23.5, 24.5, 25.5, 26.5, 27.5], doubleForBody: false, tip: 'Shoulder point to cuff' }
      }
    },
    {
      id: 'performance-merino-tee', name: 'Performance Merino Tee', category: 'Tops', type: 'upper',
      fitNote: 'Athletic fit in temperature-regulating merino. Merino has a softer drape than synthetic.',
      measurements: {
        'Chest':         { garment: [44, 46.5, 49, 51.5, 54, 56.5], doubleForBody: true, ease: 8, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [65, 66.5, 68, 69.5, 71, 72.5], doubleForBody: false, tip: 'High point shoulder to hem' },
        'Sleeve Length': { garment: [22.5, 23.5, 24.5, 25.5, 26.5, 27.5], doubleForBody: false, tip: 'Shoulder point to cuff' }
      }
    },
    {
      id: 'performance-merino-tee-long-sleeve', name: 'Performance Merino Long Sleeve', category: 'Tops', type: 'upper',
      fitNote: 'Athletic fit in merino wool. For cool-weather running and travel.',
      measurements: {
        'Chest':         { garment: [44, 46.5, 49, 51.5, 54, 56.5], doubleForBody: true, ease: 8, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [65, 66.5, 68, 69.5, 71, 72.5], doubleForBody: false, tip: 'High point shoulder to hem' },
        'Sleeve Length': { garment: [61, 62, 63, 64, 65, 66], doubleForBody: false, tip: 'Shoulder point to cuff' }
      }
    },

    /* LAYERS */
    {
      id: 'arow-base-layer', name: 'AROW Base Layer', category: 'Layers', type: 'upper',
      fitNote: 'Compression fit. The fabric stretches to fit your body snugly. If you prefer a less compressive feel, size up.',
      measurements: {
        'Chest':         { garment: [37, 40, 43, 46, 49, 52], doubleForBody: true, ease: -2, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [63, 64, 66, 68, 70, 72], doubleForBody: false, tip: 'High point shoulder to hem' },
        'Sleeve Length': { garment: [60, 62, 64, 65, 67, 69], doubleForBody: false, tip: 'Shoulder point to cuff' }
      }
    },

    /* OUTERWEAR */
    {
      id: 'grid-storm-jacket', name: 'GRID Storm Jacket', category: 'Outerwear', type: 'upper',
      fitNote: 'Performance fit with room for a base layer underneath.',
      measurements: {
        'Chest':         { garment: [46, 48, 50, 52, 54, 56], doubleForBody: true, ease: 6, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [65, 67, 69, 71, 73, 75], doubleForBody: false, tip: 'High point shoulder to hem' },
        'Sleeve Length': { garment: [63, 65, 67, 69, 71, 73], doubleForBody: false, tip: 'Shoulder point to cuff' }
      }
    },
    {
      id: 'windbreaker-jacket', name: 'Windbreaker Jacket', category: 'Outerwear', type: 'upper',
      fitNote: 'Relaxed fit. Generous cut for layering and airflow.',
      measurements: {
        'Chest':         { garment: [51, 53, 55, 57, 59, 61], doubleForBody: true, ease: 16, tip: 'Around the fullest part of your chest' },
        'Body Length':   { garment: [null, 65, 67, 69, 71, 73], doubleForBody: false, tip: 'High point shoulder to hem' },
        'Sleeve Length': { garment: [63, 65, 67, 69, 71, 73], doubleForBody: false, tip: 'Shoulder point to cuff' }
      }
    },

    /* SHORTS */
    {
      id: 'arow-marathon-shorts-v2', name: 'AROW Marathon Short V2', category: 'Shorts', type: 'lower',
      fitNote: 'Race-weight short with a drawcord elastic waistband. Waist body range reflects the waistband’s wear stretch.',
      measurements: {
        'Waist':              { garment: [26, 28.5, 31, 33.5, 36, 38.5], doubleForBody: true, ease: -18, tip: 'Measure at the natural waist (just above the belly button)' },
        'Hip':                { garment: [43, 45.5, 48, 50.5, 53, 55.5], doubleForBody: true, ease: 5, tip: 'Around the fullest part of your hips' },
        'Inseam':             { garment: [15, 15, 15, 15, 15, 15], doubleForBody: false, tip: 'Length of inner leg seam' },
        'Thigh (flat)':       { garment: [31.25, 32.75, 34.25, 35.75, 37.25, 38.75], doubleForBody: false, flatOnly: true, tip: 'Half-width at upper thigh' },
        'Leg Opening (flat)': { garment: [27.5, 28.5, 29.5, 30.5, 31.5, 32.5], doubleForBody: false, flatOnly: true, tip: 'Half-width at hem of leg' }
      }
    },
    {
      id: 'arow-sprint-short', name: 'AROW Sprint Short', category: 'Shorts', type: 'lower',
      fitNote: 'Split-leg sprint short. Stretch waistband expands substantially from its relaxed state for a secure fit.',
      measurements: {
        'Waist':                  { garment: [24.5, 27, 29.5, 32, 34.5, 37], doubleForBody: true, ease: -22, tip: 'Measure at the natural waist; waistband stretches to fit' },
        'Hip':                    { garment: [43.5, 46, 48.5, 51, 53.5, 56], doubleForBody: true, ease: 5, tip: 'Around the fullest part of your hips' },
        'Inseam':                 { garment: [8, 8, 8, 8, 8, 8], doubleForBody: false, tip: 'Length of inner leg seam' },
        'Waist stretched (flat)': { garment: [36.5, 39, 41.5, 44, 46.5, 49], doubleForBody: false, flatOnly: true, tip: 'Half-width of waistband at full stretch' },
        'Thigh (flat)':           { garment: [30, 31.5, 33, 34.5, 36, 37.5], doubleForBody: false, flatOnly: true, tip: 'Half-width at upper thigh' },
        'Leg Opening (flat)':     { garment: [28, 29.5, 31, 32.5, 34, 35.5], doubleForBody: false, flatOnly: true, tip: 'Half-width at hem of leg' }
      }
    },
    {
      id: 'arow-ultra-short-v3', name: 'AROW Ultra Short V3', category: 'Shorts', type: 'lower',
      fitNote: 'Long-course half tight with an integrated deep pocket system which sits high waisted.',
      measurements: {
        'Waist':              { garment: [27, 29.5, 32, 34.5, 37, 39.5], doubleForBody: true, ease: -16, tip: 'Measure at the natural waist (just above the belly button)' },
        'Hip':                { garment: [39, 41.5, 44, 46.5, 49, 51.5], doubleForBody: true, ease: 5, tip: 'Around the fullest part of your hips' },
        'Inseam':             { garment: [19.5, 19.5, 19.5, 19.5, 19.5, 19.5], doubleForBody: false, tip: 'Length of inner leg seam' },
        'Thigh (flat)':       { garment: [28.75, 30.25, 31.75, 33.25, 34.75, 36.25], doubleForBody: false, flatOnly: true, tip: 'Half-width at upper thigh' },
        'Leg Opening (flat)': { garment: [22.25, 23.25, 24.25, 25.25, 26.25, 27.25], doubleForBody: false, flatOnly: true, tip: 'Half-width at hem of leg' }
      }
    },

    /* TIGHTS */
    {
      id: 'arow-ultra-tight', name: 'AROW Ultra Tight', category: 'Tights', type: 'lower',
      fitNote: 'Compression tight. Snug through waist and hip; stretches to the body. If you prefer a less compressive feel, size up.',
      measurements: {
        'Waist':        { garment: [27, 29.5, 32, 34.5, 37, 39.5], doubleForBody: true, ease: -16, tip: 'Measure at the natural waist; waistband stretches to fit' },
        'Hip':          { garment: [33.5, 36, 38.5, 41, 43.5, 46], doubleForBody: true, ease: -15, tip: 'Around the fullest part of your hips; compression fabric stretches to fit' },
        'Inseam':       { garment: [67.75, 67.75, 67.75, 67.75, 67.75, 67.75], doubleForBody: false, tip: 'Length of inner leg seam' },
        'Total Length': { garment: [89.1, 89.8, 90.5, 91.2, 91.9, 92.6], doubleForBody: false, tip: 'Top of waistband to hem' },
        'Knee (flat)':  { garment: [11.5, 12.5, 13.5, 14.5, 15.5, 16.5], doubleForBody: false, flatOnly: true, tip: 'Half-width at knee (35 cm from crotch)' },
        'Calf (flat)':  { garment: [11, 11.75, 12.5, 13.25, 14, 14.75], doubleForBody: false, flatOnly: true, tip: 'Half-width at calf (17.5 cm from hem)' }
      }
    },

    /* WOMEN'S-ONLY */
    {
      id: 'arow-womens-ultra-short-v2', name: 'AROW Women’s Ultra Short V2', category: 'Women’s', type: 'lower',
      womensOnly: true,
      fitNote: 'Women’s-specific cut. Long-course half tight with an integrated deep pocket system which sits high waisted.',
      measurements: {
        'Waist':                { garment: [27.5, 30, 32.5, 35, 37.5, 40], doubleForBody: true, ease: -9, tip: 'Measure at the natural waist; waistband stretches to fit' },
        'Hip':                  { garment: [30, 32.5, 35, 37.5, 40, 42.5], doubleForBody: true, ease: -16, tip: 'Around the fullest part of your hips; fabric stretches to fit' },
        'Inseam':               { garment: [21.5, 21.5, 21.5, 21.5, 21.5, 21.5], doubleForBody: false, tip: 'Length of inner leg seam' },
        'Waist relaxed (flat)': { garment: [24.5, 27, 29.5, 32, 34.5, 37], doubleForBody: false, flatOnly: true, tip: 'Half-width of waistband at rest' },
        'Thigh (flat)':         { garment: [20, 21.5, 23, 24.5, 26, 27.5], doubleForBody: false, flatOnly: true, tip: 'Half-width at upper thigh' },
        'Leg Opening (flat)':   { garment: [13.2, 14.2, 15.2, 16.2, 17.2, 18.2], doubleForBody: false, flatOnly: true, tip: 'Half-width at hem of leg' }
      }
    },
    {
      id: 'arow-sports-bra', name: 'AROW Sports Bra', category: 'Women’s', type: 'upper',
      womensOnly: true,
      fitNote: 'Medium-support sports bra. Compression construction — body is larger than garment circumference. Sizes shown are women’s.',
      measurements: {
        'Chest':                { garment: [28, 30.5, 33, 35.5, 38, 40.5], doubleForBody: true, ease: -22, tip: 'Around the fullest part of your bust' },
        'Body Length (front)':  { garment: [24, 25, 26, 27, 28, 29], doubleForBody: false, tip: 'High point shoulder to hem (front)' },
        'Body Length (back)':   { garment: [22, 23, 24, 25, 26, 27], doubleForBody: false, tip: 'High point shoulder to hem (back)' }
      }
    }
  ];

  /* ---------- Math helpers ---------- */

  function garmentToBody(val, isDouble, ease) {
    if (val === null || val === undefined) return null;
    return isDouble ? (val * 2) - ease : val;
  }

  function buildBodyRanges(garmentArr, isDouble, ease) {
    var bodyVals = garmentArr.map(function (v) { return garmentToBody(v, isDouble, ease); });
    var ranges = [];
    for (var i = 0; i < bodyVals.length; i++) {
      if (bodyVals[i] === null) { ranges.push(null); continue; }
      var lo, hi;
      if (i === 0 || bodyVals[i - 1] === null) {
        lo = isDouble ? bodyVals[i] - 2.5 : bodyVals[i] - 1;
      } else {
        lo = (bodyVals[i - 1] + bodyVals[i]) / 2;
      }
      if (i === bodyVals.length - 1 || bodyVals[i + 1] === null) {
        hi = isDouble ? bodyVals[i] + 2.5 : bodyVals[i] + 1;
      } else {
        hi = (bodyVals[i] + bodyVals[i + 1]) / 2;
      }
      ranges.push([Math.round(lo), Math.round(hi)]);
    }
    return ranges;
  }

  function toInch(val) {
    if (val === null || val === undefined) return null;
    return Math.round(val / 2.54);
  }

  function formatRange(range, unit) {
    if (range === null) return null;
    if (Array.isArray(range)) {
      var lo = range[0], hi = range[1];
      if (unit === 'in') { lo = toInch(lo); hi = toInch(hi); }
      return lo + '–' + hi;
    }
    if (unit === 'in') return String(toInch(range));
    return String(range);
  }

  function formatSingle(val, unit) {
    if (val === null || val === undefined) return null;
    if (unit === 'in') return String(toInch(val));
    return String(Math.round(val * 2) / 2);
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  /* Persist the user's toggle choices (unit, gender, measure) across sessions.
     localStorage may throw in private mode or when storage is full — fail silently. */
  var STORAGE_PREFIX = 'chance:size-guide:';
  var ALLOWED = {
    unit:    ['cm', 'in'],
    gender:  ['unisex', 'womens'],
    measure: ['body', 'garment']
  };
  function readStored(name) {
    try {
      var v = window.localStorage.getItem(STORAGE_PREFIX + name);
      return ALLOWED[name].indexOf(v) !== -1 ? v : null;
    } catch (e) { return null; }
  }
  function writeStored(name, v) {
    try { window.localStorage.setItem(STORAGE_PREFIX + name, v); } catch (e) {}
  }

  /* Countries whose default unit is inches when the visitor has no stored
     preference. Add or remove ISO 3166-1 alpha-2 codes here. Everything not
     listed defaults to centimeters. */
  var INCH_COUNTRIES = ['US', 'LR', 'MM', 'GB'];
  function unitForCountry(code) {
    if (!code) return 'cm';
    return INCH_COUNTRIES.indexOf(String(code).toUpperCase()) !== -1 ? 'in' : 'cm';
  }

  /* Telemetry — fires only if the host page has GA / GTM / Klaviyo loaded.
     Each helper is a no-op when the relevant SDK is missing, so we never
     break a page that doesn't have analytics installed. */

  function trackSizeGuideOpen(detail) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'size_guide_open', detail);
    } else if (window.dataLayer && typeof window.dataLayer.push === 'function') {
      var payload = { event: 'size_guide_open' };
      for (var k in detail) if (Object.prototype.hasOwnProperty.call(detail, k)) payload[k] = detail[k];
      window.dataLayer.push(payload);
    }
  }

  /* Map the internal toggle value to the Klaviyo profile-property string. */
  var KLAVIYO_GENDER_MAP = { unisex: 'Male', womens: 'Female' };
  function setKlaviyoGender(toggleValue) {
    var value = KLAVIYO_GENDER_MAP[toggleValue];
    if (!value) return;
    var props = { Gender: value };
    if (window.klaviyo && typeof window.klaviyo.identify === 'function') {
      try { window.klaviyo.identify(props); } catch (e) {}
    } else {
      try {
        window._learnq = window._learnq || [];
        window._learnq.push(['identify', props]);
      } catch (e) {}
    }
  }

  /* ---------- Custom element ---------- */

  function normalizeId(id) {
    if (id === null || id === undefined) return '';
    return String(id).trim().toLowerCase();
  }

  /* Exact id -> PRODUCTS index. Measurement tables are keyed by id. */
  function productIndexById(id) {
    id = normalizeId(id);
    if (!id) return -1;
    for (var i = 0; i < PRODUCTS.length; i++) {
      if (normalizeId(PRODUCTS[i].id) === id) return i;
    }
    return -1;
  }

  /*
    Catalog alias map, parsed once from the <script data-sg-catalog> JSON that
    snippets/size-guide-catalog.liquid emits inside each <size-guide>. Lets an
    off-convention siblings handle (e.g. exclude-arow-ultra-short-3) resolve to
    the right table without editing this file. That catalog is the single source
    of truth shared with the Liquid visibility gate and the Size Guide page, so
    the gate and the chart can no longer drift apart.
  */
  var catalogAliases = null; /* { aliasHandle: canonicalId } once parsed */

  function parseCatalog(rootEl) {
    if (catalogAliases) return; /* parse once per page */
    catalogAliases = {};
    var script = rootEl && rootEl.querySelector('script[data-sg-catalog]');
    if (!script) return;
    var rows;
    try {
      rows = JSON.parse(script.textContent || '[]');
    } catch (e) {
      try { console.warn('[size-guide] could not parse catalog JSON', e); } catch (e2) {}
      return;
    }
    for (var i = 0; i < rows.length; i++) {
      var id = normalizeId(rows[i] && rows[i].id);
      if (!id) continue;
      /* Drift guard: a catalog id with no measurement table renders nothing. */
      if (productIndexById(id) === -1) {
        try { console.warn('[size-guide] catalog id "' + id + '" has no measurement table in size-guide.js'); } catch (e3) {}
      }
      var sibs = (rows[i] && rows[i].siblings) || [];
      for (var j = 0; j < sibs.length; j++) {
        var alias = normalizeId(sibs[j]);
        if (alias) catalogAliases[alias] = id;
      }
    }
  }

  var SizeGuide = function () {
    var element = Reflect.construct(HTMLElement, [], SizeGuide);
    return element;
  };

  SizeGuide.prototype = Object.create(HTMLElement.prototype);
  SizeGuide.prototype.constructor = SizeGuide;
  Object.setPrototypeOf(SizeGuide, HTMLElement);

  SizeGuide.prototype.connectedCallback = function () {
    if (this._initialized) return;
    this._initialized = true;

    this.mode = this.getAttribute('data-mode') || 'modal';

    this.assets = {
      mensTops:      this.getAttribute('data-asset-mens-tops')      || '',
      mensBottoms:   this.getAttribute('data-asset-mens-bottoms')   || '',
      womensTops:    this.getAttribute('data-asset-womens-tops')    || '',
      womensBottoms: this.getAttribute('data-asset-womens-bottoms') || ''
    };

    this.strings = {
      bodymapMensTops:      this.getAttribute('data-bodymap-alt-mens-tops')      || 'Front-facing illustration of an upper body showing where to measure.',
      bodymapMensBottoms:   this.getAttribute('data-bodymap-alt-mens-bottoms')   || 'Front-facing illustration of a lower body showing where to measure.',
      bodymapWomensTops:    this.getAttribute('data-bodymap-alt-womens-tops')    || 'Front-facing illustration of a woman’s upper body showing where to measure.',
      bodymapWomensBottoms: this.getAttribute('data-bodymap-alt-womens-bottoms') || 'Front-facing illustration of a woman’s lower body showing where to measure.',
      bodyHeader:           this.getAttribute('data-label-body-header')          || 'Body Measurements',
      garmentHeader:        this.getAttribute('data-label-garment-header')       || 'Flat Garment Measurements',
      tipShowFor:           this.getAttribute('data-label-tip-show-for')         || 'Show measurement help',
      notAvailable:         this.getAttribute('data-label-not-available')        || 'Not available',
      captionTemplate:      this.getAttribute('data-label-caption')              || 'Size measurements for {product}, {measure} in {unit}.',
      cmName:               'centimeters',
      inName:               'inches',
      bodyName:             'body',
      garmentName:          'garment'
    };

    /* DOM refs */
    this.picker     = this.querySelector('[data-sz-picker]');
    this.fitNoteEl  = this.querySelector('[data-sz-fit-note]');
    this.bodymapEl  = this.querySelector('[data-sz-bodymap]');
    this.tableEl    = this.querySelector('[data-sz-table]');
    this.statusEl   = this.querySelector('[data-sz-status]');

    /* Embedded-mode-only Shop CTA. Modal mode omits these nodes so the refs
       stay null and _renderShopButton becomes a no-op. */
    this.shopButtonWrap  = this.querySelector('[data-sz-shop-button-wrap]');
    this.shopButton      = this.querySelector('[data-sz-shop-button]');
    this.shopButtonLabel = this.querySelector('[data-sz-shop-button-label]');
    this.shopData = {};
    var shopDataEl = this.querySelector('[data-sz-shop-data]');
    if (shopDataEl) {
      try {
        this.shopData = JSON.parse(shopDataEl.textContent || '{}') || {};
      } catch (e) { this.shopData = {}; }
    }

    this.toggleGroups = {
      gender:  this.querySelector('[data-sz-toggle="gender"]'),
      unit:    this.querySelector('[data-sz-toggle="unit"]'),
      measure: this.querySelector('[data-sz-toggle="measure"]')
    };

    /* Initial state. All three toggles persist across sessions; if the visitor
       has no stored unit preference, fall back to the shop locale's default. */
    var country = this.getAttribute('data-country') || '';
    this.currentUnit    = readStored('unit')    || unitForCountry(country);
    this.currentGender  = readStored('gender')  || 'unisex';
    this.currentMeasure = readStored('measure') || 'body';

    this._syncToggleDom('unit',    this.currentUnit);
    this._syncToggleDom('gender',  this.currentGender);
    this._syncToggleDom('measure', this.currentMeasure);

    parseCatalog(this);
    var requested = normalizeId(this.getAttribute('data-default-product-id'));
    var idx = this._findProductIndex(requested);
    /* Remember the PDP product so re-opening the modal anchors back to it
       even if the visitor switched the picker to another product. */
    this._pdpProductIndex = idx;
    if (idx === -1) {
      if (requested) {
        try { console.warn('[size-guide] No product matches id "' + requested + '"; falling back to first entry.'); } catch (e) {}
      }
      idx = 0;
    }
    this.currentProductIndex = idx;

    /* Watch the mobile breakpoint so the table can flip its orientation
       when the viewport crosses 640px. `change` fires only on crossings,
       so no debounce is needed. Re-renders pass `announce: false` because
       the user did not change unit or measure (SC 4.1.3). */
    this._mq = window.matchMedia('(max-width: 640px)');
    var self = this;
    this._mqHandler = function () { self._render({ announce: false }); };
    if (this._mq.addEventListener) this._mq.addEventListener('change', this._mqHandler);
    else if (this._mq.addListener) this._mq.addListener(this._mqHandler);

    this._renderPicker();
    this._bindEvents();
    this._render();

    /* Theme editor lifecycle: when this section gets re-rendered the element is re-created, so connectedCallback runs again. */
  };

  SizeGuide.prototype.disconnectedCallback = function () {
    if (this._docClickHandler) {
      document.removeEventListener('click', this._docClickHandler, true);
      this._docClickHandler = null;
    }
    if (this._triggerEl && this._triggerClickHandler) {
      this._triggerEl.removeEventListener('click', this._triggerClickHandler);
      this._triggerEl = null;
      this._triggerClickHandler = null;
    }
    if (this._statusTimer) {
      clearTimeout(this._statusTimer);
      this._statusTimer = null;
    }
    if (this._mq && this._mqHandler) {
      if (this._mq.removeEventListener) this._mq.removeEventListener('change', this._mqHandler);
      else if (this._mq.removeListener) this._mq.removeListener(this._mqHandler);
      this._mq = null;
      this._mqHandler = null;
    }
  };

  /* ---------- Lookup ---------- */

  SizeGuide.prototype._findProductIndex = function (id) {
    id = normalizeId(id);
    if (!id) return -1;

    /* 1. Catalog alias: an off-convention siblings handle declared in
       snippets/size-guide-catalog.liquid maps straight to a guide id. */
    if (catalogAliases && catalogAliases[id]) {
      var aliased = productIndexById(catalogAliases[id]);
      if (aliased !== -1) return aliased;
    }

    /* 2. Exact id, then with the `exclude-` prefix stripped (the naming
       convention `exclude-<slug>`). Preserved as the fallback path. */
    var candidates = [id];
    if (id.indexOf('exclude-') === 0) candidates.push(id.slice('exclude-'.length));
    for (var c = 0; c < candidates.length; c++) {
      var hit = productIndexById(candidates[c]);
      if (hit !== -1) return hit;
    }
    return -1;
  };

  /* ---------- Render ---------- */

  SizeGuide.prototype._renderPicker = function () {
    if (!this.picker) return;

    var grouped = {};
    var order = [];
    for (var i = 0; i < PRODUCTS.length; i++) {
      var p = PRODUCTS[i];
      if (!grouped[p.category]) {
        grouped[p.category] = [];
        order.push(p.category);
      }
      grouped[p.category].push({ idx: i, product: p });
    }

    var html = '';
    for (var c = 0; c < order.length; c++) {
      var cat = order[c];
      html += '<optgroup label="' + escapeHtml(cat) + '">';
      var items = grouped[cat];
      for (var j = 0; j < items.length; j++) {
        var entry = items[j];
        var selected = entry.idx === this.currentProductIndex ? ' selected' : '';
        html += '<option value="' + entry.idx + '"' + selected + '>' +
                escapeHtml(entry.product.name) + '</option>';
      }
      html += '</optgroup>';
    }
    this.picker.innerHTML = html;
  };

  SizeGuide.prototype._render = function (opts) {
    opts = opts || {};
    var p = PRODUCTS[this.currentProductIndex];

    /* Women's-only products force gender = 'womens' for rendering and hide
       the gender toggle. The user's stored preference (this.currentGender)
       is preserved untouched, so picking a unisex product later restores it. */
    var effectiveGender = p.womensOnly ? 'womens' : this.currentGender;
    this._setGenderToggleVisible(!p.womensOnly);

    /* Capture focus state inside the table before re-render so a viewport
       crossing (which transposes the table) does not drop focus on a tip
       button the user is interacting with. Tip ids are stable across
       orientations, so we can re-find the same button after rendering. */
    var focusBefore = null;
    if (this.tableEl && this.tableEl.contains(document.activeElement)) {
      var active = document.activeElement;
      focusBefore = {
        id: active.id || null,
        expanded: active.getAttribute && active.getAttribute('aria-expanded') === 'true'
      };
    }

    this._renderPicker();
    this._renderFitNote(p, effectiveGender);
    this._renderBodymap(p, effectiveGender);
    this._renderTable(p);
    this._renderShopButton(p);

    if (focusBefore && focusBefore.id) {
      var restored = document.getElementById(focusBefore.id);
      if (restored && this.tableEl && this.tableEl.contains(restored)) {
        if (focusBefore.expanded && restored.hasAttribute('data-sz-tip')) {
          this._toggleTip(restored);
        }
        try { restored.focus({ preventScroll: true }); } catch (e) { restored.focus(); }
      }
    }

    /* `announce: false` is passed for resize-driven re-renders so the polite
       live region does not narrate "…body in cm" when nothing the user did
       caused a meaningful state change. */
    if (opts.announce !== false) {
      this._announceStatus(p);
    }
  };

  SizeGuide.prototype._setGenderToggleVisible = function (visible) {
    var group = this.toggleGroups && this.toggleGroups.gender;
    if (!group) return;
    var wrapper = group.closest('.sz-toggle');
    if (!wrapper) return;
    if (visible) wrapper.removeAttribute('hidden');
    else wrapper.setAttribute('hidden', '');
  };

  SizeGuide.prototype._renderFitNote = function (p, gender) {
    if (!this.fitNoteEl) return;
    var note = p.fitNote || '';
    if (gender === 'womens' && !p.womensOnly) {
      note = 'Our unisex pieces run generous on women — most women size down one from their usual. Measure yourself to confirm.';
    }
    this.fitNoteEl.textContent = note;
  };

  SizeGuide.prototype._renderBodymap = function (p, gender) {
    if (!this.bodymapEl) return;
    var isWomens = gender === 'womens';
    var isUpper = p.type === 'upper';
    var src, alt;
    if (isWomens) {
      src = isUpper ? this.assets.womensTops : this.assets.womensBottoms;
      alt = isUpper ? this.strings.bodymapWomensTops : this.strings.bodymapWomensBottoms;
    } else {
      src = isUpper ? this.assets.mensTops : this.assets.mensBottoms;
      alt = isUpper ? this.strings.bodymapMensTops : this.strings.bodymapMensBottoms;
    }
    if (!src) {
      this.bodymapEl.innerHTML = '';
      return;
    }
    this.bodymapEl.innerHTML = '<img src="' + escapeHtml(src) + '" alt="' + escapeHtml(alt) + '" loading="lazy">';
  };

  /* Viewport ≤ 640px swaps the table to a transposed orientation:
     sizes become row labels (read top-to-bottom), measurements become
     column headers. Keeps the same data and toggle behaviour; only the
     rendered HTML differs. */
  SizeGuide.prototype._isNarrow = function () {
    return !!(this._mq && this._mq.matches);
  };

  SizeGuide.prototype._renderTable = function (p) {
    if (!this.tableEl) return;
    var transposed = this._isNarrow();
    this.tableEl.classList.toggle('sz-table--transposed', transposed);
    if (transposed) this._renderTableTransposed(p);
    else this._renderTableStandard(p);
  };

  /* ---------- Render helpers shared by both table orientations ---------- */

  SizeGuide.prototype._captionText = function (p) {
    var unit = this.currentUnit;
    var measure = this.currentMeasure;
    return this.strings.captionTemplate
      .replace('{product}', p.name)
      .replace('{measure}', measure === 'body' ? this.strings.bodyName : this.strings.garmentName)
      .replace('{unit}', unit === 'cm' ? this.strings.cmName : this.strings.inName);
  };

  /* Compute the displayed value for one (measurement, size) cell.
     Returns the formatted string or null when the size is unavailable. */
  SizeGuide.prototype._formatMeasurement = function (m, sizeIdx) {
    var unit = this.currentUnit;
    var measure = this.currentMeasure;
    var raw = m.garment[sizeIdx];
    if (measure === 'garment') return formatSingle(raw, unit);
    if (m.doubleForBody) {
      var ranges = buildBodyRanges(m.garment, true, m.ease || 0);
      return formatRange(ranges[sizeIdx], unit);
    }
    return formatSingle(garmentToBody(raw, false, 0), unit);
  };

  SizeGuide.prototype._cellHtml = function (formatted) {
    if (formatted === null) {
      return '<td><span class="sz-status">' + escapeHtml(this.strings.notAvailable) + '</span><span aria-hidden="true">—</span></td>';
    }
    return '<td>' + formatted + '</td>';
  };

  /* Disclosure button + hidden help text for one measurement.
     `k` is the unfiltered index in Object.keys(p.measurements) so the
     element ids are stable across orientation flips and toggle changes. */
  SizeGuide.prototype._tipMarkup = function (key, k, m) {
    var measure = this.currentMeasure;
    var tipPrefix = (measure === 'garment' && m.doubleForBody) ? 'Flat: ' : '';
    var tipText = m.tip ? (tipPrefix + m.tip) : '';
    if (!tipText) return { btn: '', span: '' };
    var btnId = 'sz-tip-btn-' + this._instanceId() + '-' + k;
    var txtId = 'sz-tip-txt-' + this._instanceId() + '-' + k;
    return {
      btn:
        '<button type="button" class="sz-tip" ' +
          'id="' + btnId + '" ' +
          'aria-expanded="false" ' +
          'aria-controls="' + txtId + '" ' +
          'aria-label="' + escapeHtml(this.strings.tipShowFor + ': ' + key) + '" ' +
          'data-sz-tip>?' +
        '</button>',
      span: '<span id="' + txtId + '" class="sz-tip__text" hidden>' + escapeHtml(tipText) + '</span>'
    };
  };

  SizeGuide.prototype._measurementLabel = function (key, m) {
    var measure = this.currentMeasure;
    if (measure === 'body' && m.doubleForBody) return key + ' (circumference)';
    if (measure === 'garment' && m.doubleForBody) return key + ' (flat)';
    return key;
  };

  /* Filter measurements to those visible in the current measure mode.
     Returns [{ key, m, k }] where k is the unfiltered index (used for
     stable tip button ids). */
  SizeGuide.prototype._visibleMeasurements = function (p) {
    var keys = Object.keys(p.measurements);
    var measure = this.currentMeasure;
    var out = [];
    for (var k = 0; k < keys.length; k++) {
      var m = p.measurements[keys[k]];
      if (m.flatOnly && measure === 'body') continue;
      out.push({ key: keys[k], m: m, k: k });
    }
    return out;
  };

  /* ---------- Standard orientation: sizes across the top ---------- */

  SizeGuide.prototype._renderTableStandard = function (p) {
    var measure = this.currentMeasure;
    var visible = this._visibleMeasurements(p);
    var sectionLabel = measure === 'body' ? this.strings.bodyHeader : this.strings.garmentHeader;

    var html = '';
    html += '<caption>' + escapeHtml(this._captionText(p)) + '</caption>';
    html += '<thead><tr>';
    html += '<th scope="col"><span class="sz-status">Measurement</span></th>';
    for (var s = 0; s < SIZES.length; s++) {
      html += '<th scope="col">' + SIZES[s] + '</th>';
    }
    html += '</tr></thead>';

    html += '<tbody>';
    html += '<tr class="sz-table__section-header"><th scope="colgroup" colspan="' + (SIZES.length + 1) + '">' + escapeHtml(sectionLabel) + '</th></tr>';

    for (var v = 0; v < visible.length; v++) {
      var entry = visible[v];
      var label = this._measurementLabel(entry.key, entry.m);
      var tip = this._tipMarkup(entry.key, entry.k, entry.m);

      /* aria-label on the row header keeps the cell's accessible name to
         the measurement name only — without this, a screen reader would
         repeat the tip button's "?" / aria-label for every data cell in
         the row. */
      html += '<tr>';
      html += '<th scope="row" aria-label="' + escapeHtml(label) + '">' +
                '<span class="sz-th-label">' + escapeHtml(label) + '</span>' +
                tip.btn + tip.span +
              '</th>';
      for (var sz = 0; sz < SIZES.length; sz++) {
        html += this._cellHtml(this._formatMeasurement(entry.m, sz));
      }
      html += '</tr>';
    }
    html += '</tbody>';

    this.tableEl.innerHTML = html;
  };

  /* ---------- Transposed orientation: sizes down the side (mobile) ---------- */

  SizeGuide.prototype._renderTableTransposed = function (p) {
    var visible = this._visibleMeasurements(p);

    /* Caption already conveys the body-vs-garment mode ("…body in cm.")
       so the section banner row from the standard view is dropped here
       to reduce noise on a narrow viewport. */
    var html = '';
    html += '<caption>' + escapeHtml(this._captionText(p)) + '</caption>';

    html += '<thead><tr>';
    html += '<th scope="col">Size</th>';
    for (var v = 0; v < visible.length; v++) {
      var entry = visible[v];
      var label = this._measurementLabel(entry.key, entry.m);
      var tip = this._tipMarkup(entry.key, entry.k, entry.m);
      html += '<th scope="col" aria-label="' + escapeHtml(label) + '">' +
                '<span class="sz-th-label">' + escapeHtml(label) + '</span>' +
                tip.btn + tip.span +
              '</th>';
    }
    html += '</tr></thead>';

    html += '<tbody>';
    for (var s = 0; s < SIZES.length; s++) {
      html += '<tr><th scope="row">' + SIZES[s] + '</th>';
      for (var v2 = 0; v2 < visible.length; v2++) {
        html += this._cellHtml(this._formatMeasurement(visible[v2].m, s));
      }
      html += '</tr>';
    }
    html += '</tbody>';

    this.tableEl.innerHTML = html;
  };

  SizeGuide.prototype._renderShopButton = function (p) {
    /* Modal mode never renders the wrap, so bail out cleanly there. */
    if (!this.shopButtonWrap || !this.shopButton || !this.shopButtonLabel) return;

    var url = this.shopData[p.id];
    if (url) {
      this.shopButton.setAttribute('href', url);
      /* textContent (not innerHTML) — defense in depth even though product
         names are hardcoded in PRODUCTS, not user input. */
      this.shopButtonLabel.textContent = 'Shop ' + p.name;
      this.shopButtonWrap.removeAttribute('hidden');
    } else {
      /* No in-stock sibling — hide the wrap. Leave stale href/label in place;
         they're never reachable while [hidden] is set, and clearing would
         cause a brief flash on the next product switch. The picker's existing
         [data-sz-status] live region already announces the product change, so
         we deliberately do not announce the button visibility change here. */
      this.shopButtonWrap.setAttribute('hidden', '');
    }
  };

  SizeGuide.prototype._announceStatus = function (p) {
    if (!this.statusEl) return;
    if (this._statusTimer) clearTimeout(this._statusTimer);
    var msg = p.name + ', ' +
      (this.currentMeasure === 'body' ? this.strings.bodyName : this.strings.garmentName) +
      ' measurements in ' +
      (this.currentUnit === 'cm' ? this.strings.cmName : this.strings.inName) + '.';
    var self = this;
    this._statusTimer = setTimeout(function () {
      self.statusEl.textContent = msg;
    }, 250);
  };

  /* Reflect a restored value on a radiogroup's DOM. Called once at init for
     each toggle so a stored preference doesn't disagree with the markup default. */
  SizeGuide.prototype._syncToggleDom = function (name, value) {
    var group = this.toggleGroups && this.toggleGroups[name];
    if (!group) return;
    var buttons = group.querySelectorAll('[role="radio"]');
    for (var i = 0; i < buttons.length; i++) {
      var btn = buttons[i];
      var on = btn.getAttribute('data-value') === value;
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.setAttribute('tabindex', on ? '0' : '-1');
    }
  };

  SizeGuide.prototype._instanceId = function () {
    if (!this._iid) {
      this._iid = (Math.random().toString(36).slice(2, 8));
    }
    return this._iid;
  };

  /* ---------- Events ---------- */

  SizeGuide.prototype._bindEvents = function () {
    var self = this;

    if (this.picker) {
      this.picker.addEventListener('change', function () {
        var idx = parseInt(self.picker.value, 10);
        if (!isNaN(idx) && PRODUCTS[idx]) {
          self.currentProductIndex = idx;
          self._render();
        }
      });
    }

    /* Toggle radiogroups — all three persist across sessions. The gender
       toggle also writes the visitor's Klaviyo profile (Gender = Male/Female). */
    this._bindRadioGroup(this.toggleGroups.gender,  'gender',  function (val) {
      self.currentGender = val;
      writeStored('gender', val);
      setKlaviyoGender(val);
      self._render();
    });
    this._bindRadioGroup(this.toggleGroups.unit,    'unit',    function (val) {
      self.currentUnit = val;
      writeStored('unit', val);
      self._render();
    });
    this._bindRadioGroup(this.toggleGroups.measure, 'measure', function (val) {
      self.currentMeasure = val;
      writeStored('measure', val);
      self._render();
    });

    /* Disclosure tip buttons (delegated to the table). */
    if (this.tableEl) {
      this.tableEl.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-sz-tip]');
        if (!btn || !self.tableEl.contains(btn)) return;
        e.preventDefault();
        e.stopPropagation();
        self._toggleTip(btn);
      });
      this.tableEl.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var btn = self.tableEl.querySelector('[data-sz-tip][aria-expanded="true"]');
        if (btn) {
          self._closeTip(btn);
          btn.focus();
        }
      });
    }

    /* Close any open tip when clicking outside */
    this._docClickHandler = function (e) {
      if (!self.tableEl) return;
      if (e.target.closest('[data-sz-tip]')) return;
      var open = self.tableEl.querySelector('[data-sz-tip][aria-expanded="true"]');
      if (open && !self.tableEl.contains(e.target)) self._closeTip(open);
    };
    document.addEventListener('click', this._docClickHandler, true);

    /* On every modal open: reset the picker to the PDP product (so a previous
       in-modal product change doesn't carry over) and fire the GA event.
       Embedded mode has no trigger — this only runs on product pages. */
    if (this.mode === 'modal') {
      var popup = this.closest('popup-component.size-guide-popup');
      var trigger = popup && popup.querySelector('[data-popup-open]');
      if (trigger) {
        this._triggerEl = trigger;
        this._triggerClickHandler = function () {
          if (self._pdpProductIndex !== -1 &&
              self.currentProductIndex !== self._pdpProductIndex) {
            self.currentProductIndex = self._pdpProductIndex;
            self._render();
          }
          trackSizeGuideOpen({
            size_guide_id: self.getAttribute('data-default-product-id') || '',
            page_path: window.location.pathname
          });
        };
        trigger.addEventListener('click', this._triggerClickHandler);
      }
    }
  };

  SizeGuide.prototype._bindRadioGroup = function (group, name, onChange) {
    if (!group) return;
    var self = this;
    var buttons = Array.prototype.slice.call(group.querySelectorAll('[role="radio"]'));

    function activate(btn) {
      var val = btn.getAttribute('data-value');
      buttons.forEach(function (b) {
        var on = b === btn;
        b.setAttribute('aria-checked', on ? 'true' : 'false');
        b.setAttribute('tabindex', on ? '0' : '-1');
      });
      onChange(val);
    }

    group.addEventListener('click', function (e) {
      var btn = e.target.closest('[role="radio"]');
      if (!btn || !group.contains(btn)) return;
      activate(btn);
    });

    group.addEventListener('keydown', function (e) {
      var btn = e.target.closest('[role="radio"]');
      if (!btn || !group.contains(btn)) return;
      var i = buttons.indexOf(btn);
      var next = -1;
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (i + 1) % buttons.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (i - 1 + buttons.length) % buttons.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = buttons.length - 1;
          break;
        case ' ':
        case 'Enter':
          e.preventDefault();
          activate(btn);
          return;
        default:
          return;
      }
      e.preventDefault();
      var nb = buttons[next];
      activate(nb);
      nb.focus();
    });
  };

  SizeGuide.prototype._toggleTip = function (btn) {
    var open = btn.getAttribute('aria-expanded') === 'true';
    if (open) { this._closeTip(btn); return; }

    /* Close any other open tip first */
    var current = this.tableEl && this.tableEl.querySelector('[data-sz-tip][aria-expanded="true"]');
    if (current && current !== btn) this._closeTip(current);

    btn.setAttribute('aria-expanded', 'true');
    var id = btn.getAttribute('aria-controls');
    if (id) {
      var span = document.getElementById(id);
      if (span) span.removeAttribute('hidden');
    }
  };

  SizeGuide.prototype._closeTip = function (btn) {
    btn.setAttribute('aria-expanded', 'false');
    var id = btn.getAttribute('aria-controls');
    if (id) {
      var span = document.getElementById(id);
      if (span) span.setAttribute('hidden', '');
    }
  };

  customElements.define('size-guide', SizeGuide);
}());
